import type { QueryClient } from '@tanstack/react-query';

import { DEXIE_DB_NAME } from './db';
import type { KV } from './kv';

/**
 * Low-level removal of what the app stores on this origin, shared by the
 * app-key guard (another publisher's app ran here) and Settings' Clear
 * cache / Erase all content and settings.
 */

/** The query persister's database (`@shared/query-client`). */
export const QUERY_CACHE_DB_NAME = 'cf-cache';
/** The service worker's `url -> issue` index (`@shared/sw/issue-index-store`). */
export const ISSUE_INDEX_DB_NAME = 'cf-issue-index';
/** Workbox's runtime-cache timestamps (`ExpirationPlugin`). */
export const WORKBOX_EXPIRATION_DB_NAME = 'workbox-expiration';

/**
 * The databases the service worker keeps open through libraries that don't
 * close on a delete request (idb-keyval, Workbox). Deleting one leaves the
 * delete queued behind the worker's connection, and every later open of it
 * -- the reloaded app's included -- queued behind the delete, until the
 * worker stops. They are emptied instead. (Dexie databases close themselves
 * when asked to, so the worker's use of `cf-observability` is no obstacle.)
 */
const SERVICE_WORKER_DATABASES: ReadonlyArray<string> = [
	ISSUE_INDEX_DB_NAME,
	WORKBOX_EXPIRATION_DB_NAME,
	'workbox-background-sync',
];

// Used where `indexedDB.databases()` is missing.
const KNOWN_DATABASES = [
	DEXIE_DB_NAME,
	QUERY_CACHE_DB_NAME,
	ISSUE_INDEX_DB_NAME,
	WORKBOX_EXPIRATION_DB_NAME,
	'canvasflow_db',
	'canvasflow_cache',
	// The reader-event log (`@shared/events`' EVENT_LOG_DB_NAME): another
	// app's reading must not be sent as this one's.
	'cf-events',
	'cf-observability',
];

// A delete waits on every open connection; don't let one hold up the caller.
// The delete itself stays queued and runs once they close (on reload).
const DELETE_TIMEOUT_MS = 2000;

/** Every IndexedDB database on this origin. */
export async function listDatabases(): Promise<Array<string>> {
	if (typeof indexedDB === 'undefined') {
		return [];
	}
	try {
		const listed = await indexedDB.databases?.();
		if (listed) {
			return listed
				.map((database) => database.name)
				.filter((name): name is string => !!name);
		}
	} catch {
		// Fall back to the known names.
	}
	return KNOWN_DATABASES;
}

/**
 * Deletes a database, which frees its space at once (emptying it leaves the
 * files at their old size until the browser compacts them). Resolves after
 * `DELETE_TIMEOUT_MS` at most: while another connection holds it open the
 * delete waits, and every later open of it waits behind the delete -- so
 * only delete a database whose connections close on reload, never one the
 * service worker keeps open (`emptyDatabase` for those).
 */
export function deleteDatabase(name: string): Promise<void> {
	return new Promise((resolve) => {
		const timer = setTimeout(resolve, DELETE_TIMEOUT_MS);
		const done = () => {
			clearTimeout(timer);
			resolve();
		};
		try {
			const request = indexedDB.deleteDatabase(name);
			request.onsuccess = done;
			request.onerror = done;
		} catch {
			done();
		}
	});
}

/**
 * Removes every row from every store of a database, without closing or
 * deleting it: safe while other connections (the service worker's) use it.
 * Does nothing for a database that doesn't exist.
 */
export async function emptyDatabase(name: string): Promise<void> {
	if (typeof indexedDB === 'undefined' || !(await listDatabases()).includes(name)) {
		return;
	}
	await new Promise<void>((resolve) => {
		let request: IDBOpenDBRequest;
		try {
			request = indexedDB.open(name);
		} catch {
			resolve();
			return;
		}
		request.onerror = () => resolve();
		request.onblocked = () => resolve();
		request.onsuccess = () => {
			const database = request.result;
			const stores = [...database.objectStoreNames];
			if (!stores.length) {
				database.close();
				resolve();
				return;
			}
			const transaction = database.transaction(stores, 'readwrite');
			for (const store of stores) {
				transaction.objectStore(store).clear();
			}
			transaction.oncomplete = transaction.onerror = transaction.onabort = () => {
				database.close();
				resolve();
			};
		};
	});
}

/** Deletes every Cache Storage cache `remove` returns true for. */
export async function deleteCaches(
	remove: (name: string) => boolean = () => true,
): Promise<void> {
	if (typeof caches === 'undefined') {
		return;
	}
	const names = await caches.keys();
	await Promise.allSettled(
		names.filter(remove).map((name) => caches.delete(name)),
	);
}

/** True for the service worker's precache, the app's own files. */
export function isPrecache(name: string): boolean {
	return name.startsWith('workbox-precache');
}

// The service worker's media caches (`sw.ts`' CACHE_*_NAME).
const MEDIA_CACHES: ReadonlyArray<string> = ['cf-images', 'cf-videos', 'cf-audios'];

/** True for a cache of issue images, video or audio. */
export function isMediaCache(name: string): boolean {
	return MEDIA_CACHES.includes(name);
}

/**
 * On sign-in, sign-out and the OAuth callback: what came with the account
 * -- the query cache and its IndexedDB copy, the KV cache, downloaded issues
 * and the image, video and audio caches. One account's content must not stay
 * on the device for the next.
 *
 * Kept: Settings, favorites and the rest of the reader's own data, and the
 * app's own files (the precache, fonts, styles). The credentials are the
 * caller's to clear. There is no reload, so the databases are emptied, never
 * deleted: the app keeps its connections to them.
 */
export async function clearAccountData({
	queryClient,
	kv,
}: {
	queryClient: QueryClient;
	kv: KV;
}): Promise<void> {
	kv.clear();
	queryClient.clear();
	await Promise.allSettled([
		deleteCaches(isMediaCache),
		emptyDatabase(QUERY_CACHE_DB_NAME),
		emptyDatabase(DEXIE_DB_NAME),
		emptyDatabase(ISSUE_INDEX_DB_NAME),
	]);
}

/**
 * Deletes every database, except that the service worker's are emptied
 * (`SERVICE_WORKER_DATABASES`).
 */
export async function wipeDatabases(): Promise<void> {
	const names = await listDatabases();
	await Promise.allSettled(
		names.map((name) =>
			SERVICE_WORKER_DATABASES.includes(name)
				? emptyDatabase(name)
				: deleteDatabase(name),
		),
	);
}

/**
 * Everything on this origin: every cache, every database (`wipeDatabases`),
 * localStorage and sessionStorage. The caller reloads afterwards, so every
 * module starts from the empty state rather than from what it read at
 * import time.
 */
export async function wipeOrigin(): Promise<void> {
	await Promise.allSettled([deleteCaches(), wipeDatabases()]);
	try {
		localStorage.clear();
		sessionStorage.clear();
	} catch {
		// Storage unavailable: nothing more to clear.
	}
}
